import { ApiService } from '../../services/api';
import { CropEmbeddingSearchResponse, DesignResponse } from '../../types';
import {
  AddVizSimDatasetCropsRequest,
  AddVizSimDatasetCropsResponse,
  ApiMasterLabelBarcodeLookupResponse,
  ApiMasterLabelCropLookupResponse,
  ApiMasterLabelDesignLookupResponse,
  ApiMasterLabelLinkTargetsRequest,
  ApiMasterLabelLinkTargetsResponse,
  ApiMasterLabelUPCData,
  BarcodeStatus,
  CleaningChainHistoryResponse,
  CleaningChainStatsResponse,
  CleaningDashboardResponse,
  CleaningFamily,
  CleaningFinding,
  CleaningIssueScope,
  CleaningIssueType,
  CleaningItemScore,
  CleaningItemsResponse,
  CleaningLockFilter,
  CleaningLockStatus,
  CleaningRefreshRun,
  CleaningSeverity,
  CleaningSort,
  CreateDesignLockRequest,
  CropLifecycleResponse,
  DataCollectionCompleteBatchResponse,
  DataCollectionCreateRequest,
  DataCollectionCreateResponse,
  DataCollectionDashboardResponse,
  DataCollectionRequestedType,
  DataCollectionRequestRow,
  DataCollectionReviewBatchResponse,
  DataCollectionStatus,
  DataCollectionTargetScope,
  DesignLockDetailResponse,
  DesignLockListEntry,
  DesignLockListResponse,
  DesignLockMutationResponse,
  DesignLockStatus,
  DesignLockSummaryResponse,
  EnforcementMode,
  EnsureVizSimDatasetRequest,
  FetchDesignLocksRequest,
  ForgeBulkReviewStatusUpdateRequest,
  ForgeBulkReviewStatusUpdateResponse,
  ForgeDesignGroupsDashboardResponse,
  ForgeDesignGroupSearchResponse,
  ForgeDesignGroupsRefreshRequest,
  ForgeDesignGroupsScopeChangeRequest,
  ForgeDesignsDashboardResponse,
  ForgeDesignsRefreshRequest,
  ForgeReviewIssueActiveFilter,
  ForgeReviewIssueAssignmentFilter,
  ForgeReviewIssuesChainSnapshot,
  ForgeReviewIssuesDashboardResponse,
  ForgeReviewIssueSort,
  ForgeReviewSort,
  ForgeReviewStatusUpdateRequest,
  ForgeReviewStatusUpdateResponse,
  ForgeSupportedChainsResponse,
  ForgeSystemHealthDashboardResponse,
  ForgeSystemHealthRefreshRequest,
  ForgeSystemHealthRefreshRunResponse,
  ItemModPrefixEntry,
  ItemModPrefixesResponse,
  MasterDataCollectionCreateEmbeddingCropsRequest,
  MasterDataCollectionCreateEmbeddingCropsResponse,
  MasterDataCollectionDetailResponse,
  MasterDataCollectionImagePreviewResponse,
  MasterDataCollectionMasterLabelStateSaveRequest,
  MasterDataCollectionMasterLabelStateSaveResponse,
  MasterLabelCropQueryResponse,
  MasterLabelReviewIssueType,
  MasterLabelStateSaveRequest,
  MasterLabelStateSaveResponse,
  MasterLabelUnresolvedChainSnapshot,
  MasterLabelUnresolvedDashboardResponse,
  MasterLabelUnresolvedFilter,
  MasterLabelUnresolvedWorkItemsResponse,
  ModifyForgeDesignGroupsRequest,
  ModifyForgeDesignGroupsResponse,
  ProductRecognitionInferenceAggregateResponse,
  ProductRecognitionInferenceDashboardResponse,
  ProductRecognitionInferencePlotAnnotation,
  ProductRecognitionInferencePlotAnnotationRequest,
  ProductRecognitionInferencePlotAnnotationsResponse,
  ProductRecognitionInferenceRefreshRequest,
  ProductRecognitionInferenceRefreshResponse,
  ProductRecognitionInferenceRefreshRunResponse,
  RecognitionOkrAggregatesParams,
  RecognitionOkrAggregatesResponse,
  RecognitionReviewQueueParams,
  RecognitionReviewQueueResponse,
  RecognitionReviewRequest,
  RecognitionReviewSaveResponse,
  RecognitionReviewUnitDetailResponse,
  RecognitionReviewUnitParams,
  RecognitionSamplingStrategyRequest,
  RecognitionSamplingStrategyResponse,
  ReleasedVizSimCropIdsResponse,
  ReleaseVizSimDatasetRequest,
  RemoteDataCollectionChainSnapshot,
  RemoteDataCollectionConfig,
  RemoteDataCollectionConfigUpdateRequest,
  RemoteDataCollectionDailyPreviewsResponse,
  RemoteDataCollectionDashboardResponse,
  RemoteDataCollectionDefaults,
  RemoteDataCollectionImageTimelineResponse,
  RemoteDataCollectionRefreshRequest,
  RemoteDataCollectionReviewTargetsResponse,
  RemoteDataCollectionRunResponse,
  RemoteDataCollectionWorkItemDetailResponse,
  RemoteDataCollectionWorkItemsResponse,
  RemoveVizSimDatasetCropsResponse,
  ReportedIssuesFilters,
  ReportedIssuesResponse,
  SaveCheckModesResponse,
  SaveCheckTool,
  SaveCheckType,
  SaveWarning,
  SaveWarningChainStats,
  SaveWarningChainStatsResponse,
  SaveWarningReviewOutcome,
  SaveWarningsResponse,
  SaveWarningStatus,
  UpdateVizSimDatasetCropRequest,
  VizSimDatasetCrop,
  VizSimDatasetDetailResponse,
  VizSimDatasetListResponse,
  VizSimDatasetTagSuggestionsRequest,
  VizSimDatasetTagSuggestionsResponse,
} from '../types';

function normalizeApiDlUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.protocol === 'http:' && parsedUrl.hostname === 'localhost') {
      parsedUrl.hostname = '127.0.0.1';
      return parsedUrl.toString().replace(/\/$/, '');
    }
  } catch {
    return url;
  }

  return url.replace(/\/$/, '');
}

const API_DL_URL = process.env.REACT_APP_API_DL_URL
  ? normalizeApiDlUrl(process.env.REACT_APP_API_DL_URL)
  : undefined;

if (!API_DL_URL) {
  throw new Error('REACT_APP_API_DL_URL is required for Forge API requests');
}

const FORGE_ADMIN_API_URL = `${API_DL_URL}/v1/forge/admin`;
const FORGE_CROPS_API_URL = `${API_DL_URL}/v1/forge/crops`;
const FORGE_DESIGNS_API_URL = `${API_DL_URL}/v1/forge/designs`;
const FORGE_DESIGN_LOCKS_API_URL = `${API_DL_URL}/v1/forge/design-locks`;
const CONTROL_EMBEDDING_API_URL = `${API_DL_URL}/v1/control/embedding`;
const FORGE_DASHBOARDS_API_URL = `${FORGE_ADMIN_API_URL}/dashboards`;
const FORGE_DATA_COLLECTION_API_URL = `${FORGE_ADMIN_API_URL}/data-collection`;
const FORGE_DESIGN_GROUPS_API_URL = `${FORGE_ADMIN_API_URL}/design-groups`;
const FORGE_MASTER_DATA_COLLECTION_API_URL = `${FORGE_ADMIN_API_URL}/master-data-collection`;
const FORGE_MASTER_LABEL_UNRESOLVED_API_URL = `${FORGE_ADMIN_API_URL}/unresolved-master-label`;
const FORGE_MASTER_LABEL_API_URL = `${API_DL_URL}/v1/forge/labeling-tools/master-label`;
const FORGE_RECOGNITION_REVIEW_API_URL = `${FORGE_ADMIN_API_URL}/recognition-review`;
const FORGE_REMOTE_DATA_COLLECTION_API_URL = `${FORGE_ADMIN_API_URL}/remote-data-collection`;
const FORGE_VIZSIM_DATASETS_API_URL = `${FORGE_ADMIN_API_URL}/vizsim-datasets`;
const FORGE_CLEANING_API_URL = `${FORGE_ADMIN_API_URL}/cleaning-tasks`;
const FORGE_SAVE_WARNINGS_API_URL = `${FORGE_ADMIN_API_URL}/save-warnings`;

type CleaningItemsQueryParams = {
  chainId: number;
  page?: number;
  pageSize?: number;
  severity?: CleaningSeverity | 'all';
  family?: CleaningFamily;
  sort?: CleaningSort;
  search?: string;
  excluded?: 'hide' | 'only';
  duplicates?: 'hide' | 'show';
  issueTypes?: CleaningIssueType[];
  issueScope?: CleaningIssueScope | 'all';
  locks?: CleaningLockFilter;
  modPrefixes?: string[];
  /** Focus filter: items assessed on, or flagged for, these subscores. */
  focusSubscores?: string[];
  limit?: number | null;
};

function buildCleaningItemsQuery(params: CleaningItemsQueryParams): URLSearchParams {
  const query = new URLSearchParams({ chain_id: String(params.chainId) });
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.pageSize !== undefined) query.set('page_size', String(params.pageSize));
  if (params.severity !== undefined) query.set('severity', params.severity);
  if (params.family !== undefined) query.set('family', params.family);
  if (params.sort !== undefined) query.set('sort', params.sort);
  if (params.excluded !== undefined) query.set('excluded', params.excluded);
  if (params.duplicates !== undefined) query.set('duplicates', params.duplicates);
  params.issueTypes?.forEach((issueType) => query.append('issue_type', issueType));
  if (params.issueScope !== undefined) query.set('issue_scope', params.issueScope);
  if (params.locks !== undefined) query.set('locks', params.locks);
  params.modPrefixes?.forEach((prefix) => query.append('mod_prefix', prefix));
  params.focusSubscores?.forEach((subscore) => query.append('focus_subscore', subscore));
  if (params.limit !== undefined && params.limit !== null) query.set('limit', String(params.limit));
  const search = params.search?.trim();
  if (search) query.set('search', search);
  return query;
}
const REPORTED_ISSUES_API_URL = `${API_DL_URL}/v1/image/reported-issues`;

const inFlightMasterLabelUnresolvedRequests = new Map<
  string,
  Promise<MasterLabelUnresolvedDashboardResponse>
>();
const inFlightRemoteDataCollectionRequests = new Map<
  string,
  Promise<RemoteDataCollectionDashboardResponse>
>();
const inFlightRemoteDataCollectionConfigRequests = new Map<
  string,
  Promise<RemoteDataCollectionDefaults>
>();
const inFlightDesignGroupsDashboardRequests = new Map<
  string,
  Promise<ForgeDesignGroupsDashboardResponse>
>();
const inFlightReviewIssuesDashboardRequests = new Map<
  string,
  Promise<ForgeReviewIssuesDashboardResponse>
>();
const inFlightDesignsDashboardRequests = new Map<string, Promise<ForgeDesignsDashboardResponse>>();
const inFlightSystemHealthRequests = new Map<string, Promise<ForgeSystemHealthDashboardResponse>>();
const inFlightRecognitionReviewQueueRequests = new Map<
  string,
  Promise<RecognitionReviewQueueResponse>
>();
const inFlightRecognitionOkrAggregateRequests = new Map<
  string,
  Promise<RecognitionOkrAggregatesResponse>
>();
const inFlightRecognitionReviewUnitRequests = new Map<
  string,
  Promise<RecognitionReviewUnitDetailResponse>
>();

type CleaningSubscoresWire = CleaningItemScore['subscores'] & {
  class_coherence?: number | null;
  class_confusion?: number | null;
  front_split?: number | null;
  front_strays?: number | null;
  front_fragmentation?: number | null;
  front_confusion?: number | null;
  duplicate_front_classes?: number | null;
  front_identity?: number | null;
  identity_resolution?: number | null;
  review_flags?: number | null;
  metadata_integrity?: number | null;
  barcode_mix?: number | null;
  carton_confusion?: number | null;
  nonfront_confusion?: number | null;
  partial_group_front_alignment?: number | null;
  nonfront_group_alignment?: number | null;
  review_load?: number | null;
};

type CleaningFindingWire = NonNullable<CleaningItemScore['finding']> & {
  design_id?: string | null;
  issue_type?: CleaningFinding['issueType'];
  issue_scope?: CleaningFinding['issueScope'];
};

type CleaningItemCountsWire = CleaningItemScore['counts'] & {
  mixed_barcodes?: number;
  review_flagged?: number;
  metadata_issues?: number;
};

type CleaningItemScoreWire = Omit<
  CleaningItemScore,
  'subscores' | 'finding' | 'findings' | 'counts' | 'taskCoveredBy'
> & {
  exclusion_outdated?: boolean;
  previous_score?: number | null;
  previous_severity?: string | null;
  mod_prefix?: string | null;
  locked_design_count?: number | null;
  lock_status?: CleaningLockStatus | null;
  taskCoveredBy?: CleaningItemScore['taskCoveredBy'];
  task_covered_by?: CleaningItemScore['taskCoveredBy'];
  subscores: CleaningSubscoresWire;
  finding?: CleaningFindingWire | null;
  findings?: CleaningFindingWire[];
  counts: CleaningItemCountsWire;
};

function normalizeCleaningFinding(finding: CleaningFindingWire): CleaningFinding {
  const { design_id, issue_type, issue_scope, ...findingRest } = finding;
  return {
    ...findingRest,
    designId: findingRest.designId ?? design_id,
    issueType: findingRest.issueType ?? issue_type,
    issueScope: findingRest.issueScope ?? issue_scope,
  };
}

function normalizeCleaningItem(item: CleaningItemScoreWire): CleaningItemScore {
  const {
    exclusion_outdated,
    previous_score,
    previous_severity,
    mod_prefix,
    locked_design_count,
    lock_status,
    task_covered_by,
    findings,
    ...itemRest
  } = item;
  const {
    class_coherence,
    class_confusion,
    front_split,
    front_strays,
    front_fragmentation,
    front_confusion,
    duplicate_front_classes,
    front_identity,
    identity_resolution,
    review_flags,
    metadata_integrity,
    barcode_mix,
    carton_confusion,
    nonfront_confusion,
    partial_group_front_alignment,
    nonfront_group_alignment,
    review_load,
    ...subscores
  } = item.subscores;
  const { mixed_barcodes, review_flagged, metadata_issues, ...counts } = item.counts;
  const finding = item.finding ? normalizeCleaningFinding(item.finding) : item.finding;

  return {
    ...itemRest,
    exclusionOutdated: itemRest.exclusionOutdated ?? exclusion_outdated,
    previousScore: itemRest.previousScore !== undefined ? itemRest.previousScore : previous_score,
    previousSeverity:
      itemRest.previousSeverity !== undefined ? itemRest.previousSeverity : previous_severity,
    modPrefix: itemRest.modPrefix !== undefined ? itemRest.modPrefix : mod_prefix,
    lockedDesignCount:
      itemRest.lockedDesignCount !== undefined ? itemRest.lockedDesignCount : locked_design_count,
    lockStatus: itemRest.lockStatus !== undefined ? itemRest.lockStatus : lock_status,
    taskCoveredBy: itemRest.taskCoveredBy ?? task_covered_by,
    subscores: {
      ...subscores,
      classCoherence: subscores.classCoherence ?? class_coherence,
      classConfusion: subscores.classConfusion ?? class_confusion,
      frontSplit: subscores.frontSplit ?? front_split,
      frontStrays: subscores.frontStrays ?? front_strays,
      frontFragmentation: subscores.frontFragmentation ?? front_fragmentation,
      frontConfusion: subscores.frontConfusion ?? front_confusion,
      duplicateFrontClasses: subscores.duplicateFrontClasses ?? duplicate_front_classes,
      frontIdentity: subscores.frontIdentity ?? front_identity,
      identityResolution: subscores.identityResolution ?? identity_resolution,
      reviewFlags: subscores.reviewFlags ?? review_flags,
      metadataIntegrity: subscores.metadataIntegrity ?? metadata_integrity,
      barcodeMix: subscores.barcodeMix ?? barcode_mix,
      cartonConfusion: subscores.cartonConfusion ?? carton_confusion,
      nonfrontConfusion: subscores.nonfrontConfusion ?? nonfront_confusion,
      partialGroupFrontAlignment:
        subscores.partialGroupFrontAlignment ?? partial_group_front_alignment,
      nonfrontGroupAlignment: subscores.nonfrontGroupAlignment ?? nonfront_group_alignment,
      reviewLoad: subscores.reviewLoad ?? review_load,
    },
    finding,
    findings: findings?.map(normalizeCleaningFinding),
    counts: {
      ...counts,
      mixedBarcodes: counts.mixedBarcodes ?? mixed_barcodes,
      reviewFlagged: counts.reviewFlagged ?? review_flagged,
      metadataIssues: counts.metadataIssues ?? metadata_issues,
    },
  };
}

function normalizeCleaningItemsResponse(
  payload: CleaningItemsResponse & {
    issue_type_counts?: CleaningItemsResponse['issueTypeCounts'];
    issue_scope_counts?: CleaningItemsResponse['issueScopeCounts'];
    mod_prefix_counts?: CleaningItemsResponse['modPrefixCounts'];
  }
): CleaningItemsResponse {
  return {
    ...payload,
    items: payload.items.map((item) => normalizeCleaningItem(item as CleaningItemScoreWire)),
    issueTypeCounts: payload.issueTypeCounts ?? payload.issue_type_counts,
    issueScopeCounts: payload.issueScopeCounts ?? payload.issue_scope_counts,
    modPrefixCounts: payload.modPrefixCounts ?? payload.mod_prefix_counts,
  };
}

type ApiReviewStatusUpdateResponse = {
  group_id?: string;
  groupId?: string;
  is_reviewed?: boolean;
  isReviewed?: boolean;
  reviewed_by?: string | null;
  reviewedBy?: string | null;
  reviewed_at?: string | null;
  reviewedAt?: string | null;
};

function normalizeReviewStatusUpdateResponse(
  payload: ApiReviewStatusUpdateResponse,
  groupId: string,
  fallbackIsReviewed: boolean
): ForgeReviewStatusUpdateResponse {
  return {
    groupId: payload.groupId ?? payload.group_id ?? groupId,
    isReviewed: payload.isReviewed ?? payload.is_reviewed ?? fallbackIsReviewed,
    reviewedBy: payload.reviewedBy ?? payload.reviewed_by ?? null,
    reviewedAt: payload.reviewedAt ?? payload.reviewed_at ?? null,
  };
}

const inFlightProductRecognitionInferenceRequests = new Map<
  string,
  Promise<ProductRecognitionInferenceDashboardResponse>
>();
const productRecognitionInferenceDashboardCache = new Map<
  string,
  ProductRecognitionInferenceDashboardResponse
>();
const inFlightProductRecognitionInferenceAggregateRequests = new Map<
  string,
  Promise<ProductRecognitionInferenceAggregateResponse>
>();
const productRecognitionInferenceAggregateCache = new Map<
  string,
  ProductRecognitionInferenceAggregateResponse
>();
const inFlightProductRecognitionInferenceAnnotationRequests = new Map<
  string,
  Promise<ProductRecognitionInferencePlotAnnotationsResponse>
>();
let productRecognitionInferenceCacheGeneration = 0;

function clearProductRecognitionInferenceCaches(): void {
  productRecognitionInferenceCacheGeneration += 1;
  productRecognitionInferenceDashboardCache.clear();
  productRecognitionInferenceAggregateCache.clear();
  inFlightProductRecognitionInferenceRequests.clear();
  inFlightProductRecognitionInferenceAggregateRequests.clear();
  inFlightProductRecognitionInferenceAnnotationRequests.clear();
}

function filenameFromContentDisposition(value: string | null, fallback: string): string {
  if (!value) return fallback;
  const filenameStarMatch = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (filenameStarMatch?.[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1]);
    } catch {
      return fallback;
    }
  }
  const filenameMatch = value.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] ?? fallback;
}

export class ForgeApiError extends Error {
  status: number;

  detail?: string;

  rawBody?: string;

  constructor(message: string, status: number, detail?: string, rawBody?: string) {
    super(message);
    this.name = 'ForgeApiError';
    this.status = status;
    this.detail = detail;
    this.rawBody = rawBody;
  }
}

const SAVE_CHECK_WARNINGS_ERROR_CODE = 'save_check_warnings';

/** Thrown by `ForgeApiService.saveState` for the distinguishable save-check-warnings 422 (an
 * enforce-mode block on unacknowledged save checks), instead of the generic `ForgeApiError`, so
 * callers can open `SaveWarningDialog` rather than show a generic error. */
export class SaveCheckWarningsError extends Error {
  warnings: SaveWarning[];

  constructor(warnings: SaveWarning[]) {
    super('Save blocked by unacknowledged save-check warning(s)');
    this.name = 'SaveCheckWarningsError';
    this.warnings = warnings;
  }
}

type ApiErrorPayload = {
  detail?: string | { code?: string; message?: string };
  message?: string;
  errors?: Array<{ detail?: string | { code?: string; message?: string }; message?: string }>;
};

function normalizeApiErrorDetail(detail: ApiErrorPayload['detail']): string | undefined {
  if (!detail) return undefined;
  if (typeof detail === 'string') {
    try {
      const parsedDetail = JSON.parse(detail) as ApiErrorPayload['detail'];
      if (parsedDetail && typeof parsedDetail !== 'string') {
        return parsedDetail.message || parsedDetail.code || detail;
      }
    } catch {
      return detail;
    }
    return detail;
  }
  return detail.message || detail.code;
}

async function parseApiError(response: Response, fallback: string): Promise<ForgeApiError> {
  const rawBody = await response.text().catch(() => '');
  let payload: ApiErrorPayload | null = null;

  if (rawBody) {
    try {
      payload = JSON.parse(rawBody) as ApiErrorPayload;
    } catch {
      payload = null;
    }
  }

  const nestedError = payload?.errors?.find((error) => error.detail || error.message);
  const detail =
    normalizeApiErrorDetail(payload?.detail) ||
    payload?.message ||
    normalizeApiErrorDetail(nestedError?.detail) ||
    nestedError?.message ||
    rawBody ||
    undefined;
  const message = detail || `${fallback} (${response.status} ${response.statusText})`;

  return new ForgeApiError(message, response.status, detail, rawBody || undefined);
}

type SaveCheckWarningsBody = {
  error_code?: string;
  warnings?: SaveWarning[];
};

/** Detects the Master Label save's distinguishable save-check-warnings 422. The backend returns
 * this as a raw `JSONResponse` (bypassing the app's shared HTTPException reshaper), so the
 * discriminator lives at the TOP LEVEL of the body: `{ error_code: 'save_check_warnings',
 * warnings: [ <camelCase SaveWarning> ] }` — NOT under a `detail` wrapper. This distinguishes it
 * both from a generic pydantic validation 422 (whose `detail` is a list of `{loc, msg, type}`)
 * and from the app's own error envelope (`{status, code, errors: [...]}`). Returns null for every
 * other shape so the caller falls back to normal `parseApiError` handling. Takes a cloned
 * `Response` so the original body stream is still available for that fallback. */
async function parseSaveCheckWarningsError(
  response: Response
): Promise<SaveCheckWarningsError | null> {
  const rawBody = await response.text().catch(() => '');
  if (!rawBody) return null;

  let body: unknown;
  try {
    body = JSON.parse(rawBody);
  } catch {
    return null;
  }

  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const typedBody = body as SaveCheckWarningsBody;
  if (typedBody.error_code !== SAVE_CHECK_WARNINGS_ERROR_CODE) return null;
  if (!Array.isArray(typedBody.warnings)) return null;
  return new SaveCheckWarningsError(typedBody.warnings);
}

/** Shared failure handling for the two save endpoints that can return the save-check-warnings
 * 422 (`saveState` for Master Label, `saveMasterDataCollectionMasterLabelState` for Remote DC):
 * throws the typed `SaveCheckWarningsError` when a failed response matches that shape, otherwise
 * throws the generic `ForgeApiError` via `parseApiError`. No-op when `response.ok`. */
async function throwForFailedSaveResponse(
  response: Response,
  fallbackMessage: string
): Promise<void> {
  if (response.ok) return;
  if (response.status === 422) {
    const saveCheckWarningsError = await parseSaveCheckWarningsError(response.clone());
    if (saveCheckWarningsError) {
      throw saveCheckWarningsError;
    }
  }
  throw await parseApiError(response, fallbackMessage);
}

// api-dl HttpModel responses serialize with camelCase aliases (e.g. `pageSize`, `designId`),
// so the wire shapes below are camelCase even though request bodies/query params stay snake_case.
type ApiDesignLockStatusInfo = {
  designId: string;
  status: DesignLockStatus;
  cropsChanged: boolean;
  metadataChanged: boolean;
  groupsChanged: boolean;
  lockedActiveCropCount: number;
  liveActiveCropCount: number;
  lockedBy: string;
  lockedAt: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
  contentHashVersion: number;
};

type ApiDesignLockListEntry = ApiDesignLockStatusInfo & {
  normalizedBarcode?: string | null;
  representativeCropId?: string | null;
  representativeVizUrl?: string | null;
  cropCount: number;
};

type ApiDesignLockListResponse = {
  locks: ApiDesignLockListEntry[];
  total: number;
  page: number;
  pageSize: number;
};

function mapDesignLockStatusInfo(lock: ApiDesignLockStatusInfo): DesignLockMutationResponse {
  return {
    designId: lock.designId,
    status: lock.status,
    cropsChanged: lock.cropsChanged,
    metadataChanged: lock.metadataChanged,
    groupsChanged: lock.groupsChanged,
    lockedActiveCropCount: lock.lockedActiveCropCount,
    liveActiveCropCount: lock.liveActiveCropCount,
    lockedBy: lock.lockedBy,
    lockedAt: lock.lockedAt,
    updatedBy: lock.updatedBy,
    updatedAt: lock.updatedAt,
    contentHashVersion: lock.contentHashVersion,
  };
}

function mapDesignLockListEntry(lock: ApiDesignLockListEntry): DesignLockListEntry {
  return {
    ...mapDesignLockStatusInfo(lock),
    normalizedBarcode: lock.normalizedBarcode ?? '',
    representativeCropId: lock.representativeCropId ?? '',
    representativeVizUrl: lock.representativeVizUrl,
    cropCount: lock.cropCount,
  };
}

export class ForgeApiService {
  static async fetchSupportedChains(token: string): Promise<ForgeSupportedChainsResponse> {
    const response = await fetch(`${FORGE_ADMIN_API_URL}/supported-chains`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching supported Forge chains');
    }
    return response.json();
  }

  static async fetchDataCollectionDashboard(
    token: string,
    filters?: {
      status?: DataCollectionStatus | 'all';
      chainId?: number | null;
      targetScope?: DataCollectionTargetScope | 'all' | null;
      requestedType?: DataCollectionRequestedType | 'all' | null;
      requestedBy?: string | 'all' | null;
      groupBy?: 'item_number' | null;
      storeId?: number | null;
    }
  ): Promise<DataCollectionDashboardResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.chainId) params.set('chain_id', String(filters.chainId));
    if (filters?.storeId) params.set('store_id', String(filters.storeId));
    if (filters?.targetScope && filters.targetScope !== 'all') {
      params.set('target_scope', filters.targetScope);
    }
    if (filters?.requestedType && filters.requestedType !== 'all') {
      params.set('requested_type', filters.requestedType);
    }
    if (filters?.requestedBy && filters.requestedBy !== 'all') {
      params.set('requested_by', filters.requestedBy);
    }
    if (filters?.groupBy) params.set('group_by', filters.groupBy);
    const query = params.toString();
    const response = await fetch(
      `${FORGE_DASHBOARDS_API_URL}/data-collection${query ? `?${query}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching data collection dashboard');
    }
    return response.json();
  }

  static async fetchReportedIssues(
    token: string,
    filters?: ReportedIssuesFilters
  ): Promise<ReportedIssuesResponse> {
    const params = new URLSearchParams();
    if (filters?.reason) params.set('reason', filters.reason);
    if (filters?.source) params.set('source', filters.source);
    if (filters?.imageId) params.set('image_id', filters.imageId);
    if (filters?.chainId != null) params.set('chain_id', String(filters.chainId));
    if (filters?.storeId != null) params.set('store_id', String(filters.storeId));
    if (filters?.skip != null) params.set('skip', String(filters.skip));
    if (filters?.limit != null) params.set('limit', String(filters.limit));
    const query = params.toString();
    const response = await fetch(`${REPORTED_ISSUES_API_URL}${query ? `?${query}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching reported issues');
    }
    return response.json();
  }

  static async createDataCollectionRequest(
    token: string,
    request: DataCollectionCreateRequest
  ): Promise<DataCollectionCreateResponse> {
    const response = await fetch(`${FORGE_DATA_COLLECTION_API_URL}/requests`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error creating data collection request');
    }
    return response.json();
  }

  static async completeDataCollectionRequest(
    token: string,
    requestId: string
  ): Promise<DataCollectionRequestRow> {
    const response = await fetch(
      `${FORGE_DATA_COLLECTION_API_URL}/requests/${encodeURIComponent(requestId)}/complete`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error completing data collection request');
    }
    return response.json();
  }

  static async completeDataCollectionRequestsBatch(
    token: string,
    requestIds: string[]
  ): Promise<DataCollectionCompleteBatchResponse> {
    const response = await fetch(`${FORGE_DATA_COLLECTION_API_URL}/requests/complete-batch`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ request_ids: requestIds }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error completing data collection requests');
    }
    return response.json();
  }

  static async reviewDataCollectionRequest(
    token: string,
    requestId: string
  ): Promise<DataCollectionRequestRow> {
    const response = await fetch(
      `${FORGE_DATA_COLLECTION_API_URL}/requests/${encodeURIComponent(requestId)}/review`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error reviewing data collection request');
    }
    return response.json();
  }

  static async reviewDataCollectionRequestsBatch(
    token: string,
    requestIds: string[]
  ): Promise<DataCollectionReviewBatchResponse> {
    const response = await fetch(`${FORGE_DATA_COLLECTION_API_URL}/requests/review-batch`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ request_ids: requestIds }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error reviewing data collection requests');
    }
    return response.json();
  }

  static async fetchMasterLabelUnresolved(
    token: string
  ): Promise<MasterLabelUnresolvedDashboardResponse> {
    const existingRequest = inFlightMasterLabelUnresolvedRequests.get(token);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<MasterLabelUnresolvedDashboardResponse> => {
      const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/master-label-unresolved`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Master Label unresolved list');
      }
      return response.json();
    })();

    inFlightMasterLabelUnresolvedRequests.set(token, request);
    try {
      return await request;
    } finally {
      inFlightMasterLabelUnresolvedRequests.delete(token);
    }
  }

  static async refreshMasterLabelUnresolved(
    token: string,
    chainId: number
  ): Promise<MasterLabelUnresolvedChainSnapshot> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/master-label-unresolved/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chain_id: chainId,
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing Master Label unresolved snapshot');
    }
    return response.json();
  }

  static async fetchMasterLabelUnresolvedWorkItems(
    token: string,
    params?: {
      chainId?: number | null;
      unresolvedFilter?: MasterLabelUnresolvedFilter;
      page?: number;
      limit?: number;
      signal?: AbortSignal;
    }
  ): Promise<MasterLabelUnresolvedWorkItemsResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.unresolvedFilter) query.set('unresolved_filter', params.unresolvedFilter);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    const response = await fetch(
      `${FORGE_MASTER_LABEL_UNRESOLVED_API_URL}/work-items${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Master Label unresolved work items');
    }
    return response.json();
  }

  static async exportMasterLabelUnresolvedWorkItems(
    token: string,
    params?: { chainId?: number | null; unresolvedFilter?: MasterLabelUnresolvedFilter }
  ): Promise<{ blob: Blob; filename: string }> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.unresolvedFilter) query.set('unresolved_filter', params.unresolvedFilter);
    const response = await fetch(
      `${FORGE_MASTER_LABEL_UNRESOLVED_API_URL}/work-items/export${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error exporting Master Label unresolved work items');
    }
    return {
      blob: await response.blob(),
      filename: filenameFromContentDisposition(
        response.headers.get('content-disposition'),
        'master-label-unresolved-work-items.csv'
      ),
    };
  }

  static async fetchCleaningDashboard(token: string): Promise<CleaningDashboardResponse> {
    const response = await fetch(`${FORGE_CLEANING_API_URL}/dashboard`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching cleaning tasks dashboard');
    }
    return response.json();
  }

  static async fetchCleaningItems(
    token: string,
    params: CleaningItemsQueryParams
  ): Promise<CleaningItemsResponse> {
    const query = buildCleaningItemsQuery(params);
    const response = await fetch(`${FORGE_CLEANING_API_URL}/items?${query.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching cleaning tasks items');
    }
    return normalizeCleaningItemsResponse(await response.json());
  }

  static async exportCleaningItems(
    token: string,
    params: CleaningItemsQueryParams
  ): Promise<{ blob: Blob; filename: string }> {
    const query = buildCleaningItemsQuery(params);
    const response = await fetch(`${FORGE_CLEANING_API_URL}/items/export?${query.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error exporting cleaning tasks items');
    }
    return {
      blob: await response.blob(),
      filename: filenameFromContentDisposition(
        response.headers.get('content-disposition'),
        `cleaning-items-chain-${params.chainId}-${params.family ?? 'combined'}.csv`
      ),
    };
  }

  static async setCleaningItemExcluded(
    token: string,
    chainId: number,
    itemKey: string,
    excluded: boolean
  ): Promise<{ chain_id: number; item_key: string; excluded: boolean }> {
    const response = await fetch(`${FORGE_CLEANING_API_URL}/exclusions`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chain_id: chainId,
        item_key: itemKey,
        excluded,
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating cleaning item exclusion');
    }
    return response.json();
  }

  /** Completed refresh runs for one chain (oldest first); null on 404 (old API). */
  static async fetchCleaningChainHistory(
    token: string,
    chainId: number,
    limit = 60
  ): Promise<CleaningChainHistoryResponse | null> {
    const response = await fetch(
      `${FORGE_CLEANING_API_URL}/chains/${chainId}/history?limit=${limit}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (response.status === 404) return null;
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching cleaning tasks chain history');
    }
    return response.json();
  }

  /** Per-subscore aggregates for one chain; null when no snapshot is cached (404). */
  static async fetchCleaningChainStats(
    token: string,
    chainId: number
  ): Promise<CleaningChainStatsResponse | null> {
    const response = await fetch(`${FORGE_CLEANING_API_URL}/chains/${chainId}/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 404) return null;
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching cleaning tasks chain stats');
    }
    return response.json();
  }

  static async refreshCleaningChain(token: string, chainId: number): Promise<CleaningRefreshRun> {
    const response = await fetch(`${FORGE_CLEANING_API_URL}/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chain_id: chainId,
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing cleaning tasks chain');
    }
    return response.json();
  }

  static async fetchCleaningRun(token: string, runId: string): Promise<CleaningRefreshRun> {
    const response = await fetch(`${FORGE_CLEANING_API_URL}/runs/${encodeURIComponent(runId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching cleaning tasks refresh run');
    }
    return response.json();
  }

  static async fetchRemoteDataCollection(
    token: string
  ): Promise<RemoteDataCollectionDashboardResponse> {
    const existingRequest = inFlightRemoteDataCollectionRequests.get(token);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<RemoteDataCollectionDashboardResponse> => {
      const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/remote-data-collection`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching remote data collection dashboard');
      }
      return response.json();
    })();

    inFlightRemoteDataCollectionRequests.set(token, request);
    try {
      return await request;
    } finally {
      inFlightRemoteDataCollectionRequests.delete(token);
    }
  }

  static async refreshRemoteDataCollection(
    token: string,
    request: RemoteDataCollectionRefreshRequest
  ): Promise<RemoteDataCollectionChainSnapshot> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/remote-data-collection/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing remote data collection snapshot');
    }
    return response.json();
  }

  static async fetchRemoteDataCollectionConfig(
    token: string,
    params?: { nonAdminPreview?: boolean }
  ): Promise<RemoteDataCollectionDefaults> {
    const requestKey = `${token}:${params?.nonAdminPreview ? 'non-admin-preview' : 'default'}`;
    const existingRequest = inFlightRemoteDataCollectionConfigRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<RemoteDataCollectionDefaults> => {
      const query = new URLSearchParams();
      if (params?.nonAdminPreview) query.set('non_admin_preview', 'true');
      const response = await fetch(
        `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/config${query.toString() ? `?${query.toString()}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Remote DC configuration');
      }
      return response.json();
    })();

    inFlightRemoteDataCollectionConfigRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightRemoteDataCollectionConfigRequests.delete(requestKey);
    }
  }

  static async fetchRemoteDataCollectionConfigDefaults(
    token: string
  ): Promise<RemoteDataCollectionConfig> {
    const response = await fetch(`${FORGE_REMOTE_DATA_COLLECTION_API_URL}/config/defaults`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Remote DC configuration');
    }
    return response.json();
  }

  static async updateRemoteDataCollectionConfigDefaults(
    token: string,
    request: RemoteDataCollectionConfigUpdateRequest
  ): Promise<RemoteDataCollectionConfig> {
    const response = await fetch(`${FORGE_REMOTE_DATA_COLLECTION_API_URL}/config/defaults`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating Remote DC configuration');
    }
    return response.json();
  }

  static async exportRemoteDataCollectionWorkItems(
    token: string,
    params: { chainId: number; taskPrefix?: string | null }
  ): Promise<{ blob: Blob; filename: string }> {
    const query = new URLSearchParams();
    query.set('chain_id', String(params.chainId));
    if (params?.taskPrefix) query.set('task_prefix', params.taskPrefix);
    const response = await fetch(
      `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/work-items/export${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error exporting Remote DC work items');
    }
    return {
      blob: await response.blob(),
      filename: `Remote DC ${params.chainId}.csv`,
    };
  }

  static async fetchDesignsDashboard(
    token: string,
    filters?: {
      chainId?: number | null;
      barcodeStatus?: BarcodeStatus | null;
      query?: string | null;
      updatedBy?: string | null;
      updatedByValues?: string[] | null;
      createdByValues?: string[] | null;
      groupByNormalizedBarcode?: boolean;
      reviewSort?: ForgeReviewSort | null;
      page?: number;
      limit?: number;
    }
  ): Promise<ForgeDesignsDashboardResponse> {
    const params = new URLSearchParams();
    if (filters?.chainId) params.set('chain_id', String(filters.chainId));
    if (filters?.barcodeStatus) params.set('barcode_status', filters.barcodeStatus);
    if (filters?.query) params.set('query', filters.query);
    if (filters?.updatedBy) params.set('updated_by', filters.updatedBy);
    filters?.updatedByValues?.forEach((value) => params.append('updated_by_values', value));
    filters?.createdByValues?.forEach((value) => params.append('created_by_values', value));
    if (filters?.groupByNormalizedBarcode) params.set('group_by_normalized_barcode', 'true');
    if (filters?.reviewSort) params.set('review_sort', filters.reviewSort);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));
    const query = params.toString();
    const requestKey = `${token}:${query}`;
    const existingRequest = inFlightDesignsDashboardRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<ForgeDesignsDashboardResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/designs${query ? `?${query}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Forge designs dashboard');
      }
      return response.json();
    })();

    inFlightDesignsDashboardRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightDesignsDashboardRequests.delete(requestKey);
    }
  }

  static async refreshDesignsDashboard(
    token: string,
    request: ForgeDesignsRefreshRequest
  ): Promise<ForgeDesignsDashboardResponse> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/designs/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing Forge designs dashboard');
    }
    return response.json();
  }

  static async updateDesignReviewStatus(
    token: string,
    designId: string,
    request: ForgeReviewStatusUpdateRequest
  ): Promise<DesignResponse> {
    const response = await fetch(`${FORGE_DESIGNS_API_URL}/${designId}/review`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating Forge design review status');
    }
    return response.json();
  }

  static async updateDesignReviewStatusBulk(
    token: string,
    request: ForgeBulkReviewStatusUpdateRequest
  ): Promise<ForgeBulkReviewStatusUpdateResponse> {
    const response = await fetch(`${FORGE_DESIGNS_API_URL}/review/bulk`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating Forge Design review status');
    }
    return response.json();
  }

  static async fetchDesignLocks(
    token: string,
    filters: FetchDesignLocksRequest = {}
  ): Promise<DesignLockListResponse> {
    const params = new URLSearchParams();
    if (filters.chainId !== undefined && filters.chainId !== null) {
      params.set('chain_id', String(filters.chainId));
    }
    if (filters.status) params.set('status', filters.status);
    const search = filters.search?.trim();
    if (search) params.set('search', search);
    if (filters.page !== undefined) params.set('page', String(filters.page));
    if (filters.pageSize !== undefined) params.set('page_size', String(filters.pageSize));
    const query = params.toString();
    const response = await fetch(`${FORGE_DESIGN_LOCKS_API_URL}${query ? `?${query}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching design locks');
    }
    const payload = (await response.json()) as ApiDesignLockListResponse | ApiDesignLockListEntry[];
    if (Array.isArray(payload)) {
      return {
        locks: payload.map(mapDesignLockListEntry),
        total: payload.length,
        page: filters.page ?? 1,
        pageSize: filters.pageSize ?? payload.length,
      };
    }
    return {
      locks: payload.locks.map(mapDesignLockListEntry),
      total: payload.total,
      page: payload.page,
      pageSize: payload.pageSize,
    };
  }

  static async fetchItemModPrefixes(
    token: string,
    chainId: number,
    itemNumbers: string[]
  ): Promise<ItemModPrefixesResponse> {
    const query = new URLSearchParams({ chain_id: String(chainId) });
    itemNumbers.forEach((itemNumber) => query.append('item_number', itemNumber));
    const response = await fetch(`${FORGE_ADMIN_API_URL}/item-mod-prefixes?${query.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item mod prefixes');
    }
    return response.json();
  }

  static async setItemModPrefix(
    token: string,
    chainId: number,
    itemNumber: string,
    modPrefix: string
  ): Promise<ItemModPrefixEntry> {
    const response = await fetch(`${FORGE_ADMIN_API_URL}/item-mod-prefixes`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ chain_id: chainId, item_number: itemNumber, mod_prefix: modPrefix }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error saving item mod prefix');
    }
    return response.json();
  }

  static async fetchDesignLockSummary(token: string): Promise<DesignLockSummaryResponse> {
    const response = await fetch(`${FORGE_DESIGN_LOCKS_API_URL}/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching design lock summary');
    }
    return response.json();
  }

  static async createDesignLock(
    token: string,
    request: CreateDesignLockRequest
  ): Promise<DesignLockMutationResponse> {
    const response = await fetch(FORGE_DESIGN_LOCKS_API_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error creating design lock');
    }
    return mapDesignLockStatusInfo((await response.json()) as ApiDesignLockStatusInfo);
  }

  static async updateDesignLock(
    token: string,
    designId: string,
    chainId: number
  ): Promise<DesignLockMutationResponse> {
    const params = new URLSearchParams({ chain_id: String(chainId) });
    const response = await fetch(
      `${FORGE_DESIGN_LOCKS_API_URL}/${encodeURIComponent(designId)}?${params}`,
      {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating design lock');
    }
    return mapDesignLockStatusInfo((await response.json()) as ApiDesignLockStatusInfo);
  }

  static async deleteDesignLock(token: string, designId: string, chainId: number): Promise<void> {
    const params = new URLSearchParams({ chain_id: String(chainId) });
    const response = await fetch(
      `${FORGE_DESIGN_LOCKS_API_URL}/${encodeURIComponent(designId)}?${params}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error deleting design lock');
    }
  }

  static async fetchDesignLock(
    token: string,
    designId: string,
    filters: { chainId: number; includeCrops?: boolean }
  ): Promise<DesignLockDetailResponse> {
    const params = new URLSearchParams({
      chain_id: String(filters.chainId),
      include_crops: String(filters.includeCrops ?? false),
    });
    const response = await fetch(
      `${FORGE_DESIGN_LOCKS_API_URL}/${encodeURIComponent(designId)}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching design lock');
    }
    return mapDesignLockListEntry((await response.json()) as ApiDesignLockListEntry);
  }

  static async fetchVizSimDatasets(
    token: string,
    filters?: {
      chainId?: number | null;
      page?: number;
      limit?: number;
    }
  ): Promise<VizSimDatasetListResponse> {
    const params = new URLSearchParams();
    if (filters?.chainId) params.set('chain_id', String(filters.chainId));
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));
    const query = params.toString();
    const response = await fetch(`${FORGE_VIZSIM_DATASETS_API_URL}${query ? `?${query}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching VizSim datasets');
    }
    return response.json();
  }

  static async fetchVizSimDataset(
    token: string,
    datasetId: string,
    filters?: {
      releaseMajor?: number | null;
      releaseMinor?: number | null;
      label?: string | null;
      tag?: string | null;
      classHash?: string | null;
      samplePage?: number;
      sampleLimit?: number;
    }
  ): Promise<VizSimDatasetDetailResponse> {
    const params = new URLSearchParams();
    if (filters?.releaseMajor !== undefined && filters.releaseMajor !== null) {
      params.set('release_major', String(filters.releaseMajor));
    }
    if (filters?.releaseMinor !== undefined && filters.releaseMinor !== null) {
      params.set('release_minor', String(filters.releaseMinor));
    }
    if (filters?.label) params.set('label', filters.label);
    if (filters?.tag) params.set('tag', filters.tag);
    if (filters?.classHash) params.set('class_hash', filters.classHash);
    if (filters?.samplePage) params.set('sample_page', String(filters.samplePage));
    if (filters?.sampleLimit) params.set('sample_limit', String(filters.sampleLimit));
    const query = params.toString();
    const response = await fetch(
      `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}${
        query ? `?${query}` : ''
      }`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching VizSim dataset');
    }
    return response.json();
  }

  static async createVizSimDataset(
    token: string,
    request: EnsureVizSimDatasetRequest
  ): Promise<VizSimDatasetDetailResponse> {
    const response = await fetch(FORGE_VIZSIM_DATASETS_API_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error creating VizSim dataset');
    }
    return response.json();
  }

  static async addVizSimDatasetCrops(
    token: string,
    datasetId: string,
    request: AddVizSimDatasetCropsRequest
  ): Promise<AddVizSimDatasetCropsResponse> {
    const response = await fetch(
      `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}/crops`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error adding crops to VizSim dataset');
    }
    return response.json();
  }

  static async fetchVizSimDatasetTagSuggestions(
    token: string,
    request: VizSimDatasetTagSuggestionsRequest
  ): Promise<VizSimDatasetTagSuggestionsResponse> {
    const response = await fetch(`${FORGE_VIZSIM_DATASETS_API_URL}/tag-suggestions`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching VizSim tag suggestions');
    }
    return response.json();
  }

  static async addVizSimDatasetChainCrops(
    token: string,
    chainId: number,
    request: AddVizSimDatasetCropsRequest
  ): Promise<AddVizSimDatasetCropsResponse> {
    const response = await fetch(
      `${FORGE_VIZSIM_DATASETS_API_URL}/chains/${encodeURIComponent(String(chainId))}/crops`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error adding crops to VizSim dataset');
    }
    return response.json();
  }

  static async updateVizSimDatasetCrop(
    token: string,
    datasetId: string,
    cropId: string,
    request: UpdateVizSimDatasetCropRequest
  ): Promise<VizSimDatasetCrop> {
    const response = await fetch(
      `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}/crops/${encodeURIComponent(
        cropId
      )}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating VizSim dataset crop');
    }
    return response.json();
  }

  static async removeVizSimDatasetCrop(
    token: string,
    datasetId: string,
    cropId: string,
    scope: 'crop' | 'class'
  ): Promise<RemoveVizSimDatasetCropsResponse> {
    const params = new URLSearchParams({ scope });
    const response = await fetch(
      `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}/crops/${encodeURIComponent(
        cropId
      )}?${params.toString()}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error removing VizSim dataset crop');
    }
    return response.json();
  }

  static async releaseVizSimDataset(
    token: string,
    datasetId: string,
    request: ReleaseVizSimDatasetRequest
  ): Promise<VizSimDatasetDetailResponse> {
    const response = await fetch(
      `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}/releases`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error releasing VizSim dataset');
    }
    return response.json();
  }

  static async fetchReleasedVizSimCropIds(
    token: string,
    filters?: {
      chainId?: number | null;
      datasetId?: string | null;
      label?: string | null;
      tag?: string | null;
    }
  ): Promise<ReleasedVizSimCropIdsResponse> {
    const params = new URLSearchParams();
    if (filters?.chainId) params.set('chain_id', String(filters.chainId));
    if (filters?.datasetId) params.set('dataset_id', filters.datasetId);
    if (filters?.label) params.set('label', filters.label);
    if (filters?.tag) params.set('tag', filters.tag);
    const query = params.toString();
    const response = await fetch(
      `${FORGE_VIZSIM_DATASETS_API_URL}/released-crop-ids${query ? `?${query}` : ''}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching released VizSim crop ids');
    }
    return response.json();
  }

  static async fetchDesignGroupsDashboard(
    token: string,
    filters?: {
      chainId?: number | null;
      scope?: string | null;
      query?: string | null;
      updatedBy?: string | null;
      updatedByValues?: string[] | null;
      reviewSort?: ForgeReviewSort | null;
      page?: number;
      limit?: number;
    }
  ): Promise<ForgeDesignGroupsDashboardResponse> {
    const params = new URLSearchParams();
    if (filters?.chainId) params.set('chain_id', String(filters.chainId));
    if (filters?.scope) params.set('scope', filters.scope);
    if (filters?.query) params.set('query', filters.query);
    if (filters?.updatedBy) params.set('updated_by', filters.updatedBy);
    filters?.updatedByValues?.forEach((value) => params.append('updated_by_values', value));
    if (filters?.reviewSort) params.set('review_sort', filters.reviewSort);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));
    const query = params.toString();
    const requestKey = `${token}:${query}`;
    const existingRequest = inFlightDesignGroupsDashboardRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<ForgeDesignGroupsDashboardResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/design-groups${query ? `?${query}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Forge design groups dashboard');
      }
      return response.json();
    })();

    inFlightDesignGroupsDashboardRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightDesignGroupsDashboardRequests.delete(requestKey);
    }
  }

  static async fetchReviewIssuesDashboard(
    token: string,
    filters?: {
      chainId?: number | null;
      issueType?: MasterLabelReviewIssueType | null;
      assignmentFilter?: ForgeReviewIssueAssignmentFilter | null;
      activeFilter?: ForgeReviewIssueActiveFilter | null;
      query?: string | null;
      updatedByValues?: string[] | null;
      sort?: ForgeReviewIssueSort | null;
      page?: number;
      limit?: number;
    }
  ): Promise<ForgeReviewIssuesDashboardResponse> {
    const params = new URLSearchParams();
    if (filters?.chainId) params.set('chain_id', String(filters.chainId));
    if (filters?.issueType) params.set('issue_type', filters.issueType);
    if (filters?.assignmentFilter) params.set('assignment_filter', filters.assignmentFilter);
    if (filters?.activeFilter) params.set('active_filter', filters.activeFilter);
    if (filters?.query) params.set('query', filters.query);
    filters?.updatedByValues?.forEach((value) => params.append('updated_by_values', value));
    if (filters?.sort) params.set('sort', filters.sort);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));
    const query = params.toString();
    const requestKey = `${token}:${query}`;
    const existingRequest = inFlightReviewIssuesDashboardRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<ForgeReviewIssuesDashboardResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/review-issues${query ? `?${query}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Forge review issues dashboard');
      }
      return response.json();
    })();

    inFlightReviewIssuesDashboardRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightReviewIssuesDashboardRequests.delete(requestKey);
    }
  }

  static async refreshReviewIssuesDashboard(
    token: string,
    chainId: number
  ): Promise<ForgeReviewIssuesChainSnapshot> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/review-issues/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chain_id: chainId,
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing Forge review issues snapshot');
    }
    return response.json();
  }

  static async deleteCrops(token: string, cropIds: string[]): Promise<CropLifecycleResponse> {
    const response = await fetch(FORGE_CROPS_API_URL, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ crop_ids: cropIds }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error deleting crops');
    }
    return response.json();
  }

  static async refreshDesignGroupsDashboard(
    token: string,
    request: ForgeDesignGroupsRefreshRequest
  ): Promise<ForgeDesignGroupsDashboardResponse> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/design-groups/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing Forge design groups dashboard');
    }
    return response.json();
  }

  static async updateDesignGroupScope(
    token: string,
    groupId: string,
    request: ForgeDesignGroupsScopeChangeRequest
  ): Promise<ForgeDesignGroupsDashboardResponse> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/design-groups/${groupId}/scope`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error changing Forge design group type');
    }
    return response.json();
  }

  static async updateDesignGroupReviewStatus(
    token: string,
    groupId: string,
    request: ForgeReviewStatusUpdateRequest
  ): Promise<ForgeReviewStatusUpdateResponse> {
    const response = await fetch(`${FORGE_DESIGN_GROUPS_API_URL}/${groupId}/review`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating Forge design group review status');
    }
    return normalizeReviewStatusUpdateResponse(await response.json(), groupId, request.is_reviewed);
  }

  static async fetchSystemHealth(
    token: string,
    chainId?: number | null
  ): Promise<ForgeSystemHealthDashboardResponse> {
    const requestKey = `${token}:${chainId ?? 'all'}`;
    const existingRequest = inFlightSystemHealthRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams();
    if (chainId) params.set('chain_id', String(chainId));
    const query = params.toString();

    const request = (async (): Promise<ForgeSystemHealthDashboardResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/system-health${query ? `?${query}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Forge system health dashboard');
      }
      return response.json();
    })();

    inFlightSystemHealthRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightSystemHealthRequests.delete(requestKey);
    }
  }

  static async refreshSystemHealth(
    token: string,
    request: ForgeSystemHealthRefreshRequest
  ): Promise<ForgeSystemHealthDashboardResponse> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/system-health/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing Forge system health dashboard');
    }
    return response.json();
  }

  static async createSystemHealthRefreshRun(
    token: string,
    request: ForgeSystemHealthRefreshRequest
  ): Promise<ForgeSystemHealthRefreshRunResponse> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/system-health/runs`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error starting Forge system health refresh');
    }
    return response.json();
  }

  static async fetchSystemHealthRefreshRun(
    token: string,
    runId: string,
    params?: { signal?: AbortSignal }
  ): Promise<ForgeSystemHealthRefreshRunResponse> {
    const response = await fetch(
      `${FORGE_DASHBOARDS_API_URL}/system-health/runs/${encodeURIComponent(runId)}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Forge system health refresh');
    }
    return response.json();
  }

  static async fetchRemoteDataCollectionWorkItems(
    token: string,
    params?: {
      chainId?: number | null;
      taskPrefix?: string | null;
      page?: number;
      limit?: number;
      signal?: AbortSignal;
    }
  ): Promise<RemoteDataCollectionWorkItemsResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.taskPrefix) query.set('task_prefix', params.taskPrefix);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    const response = await fetch(
      `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/work-items${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching remote data collection work items');
    }
    return response.json();
  }

  static async fetchRemoteDataCollectionWorkItem(
    token: string,
    workItemId: string,
    params?: { signal?: AbortSignal }
  ): Promise<RemoteDataCollectionWorkItemDetailResponse> {
    const response = await fetch(
      `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/work-items/${encodeURIComponent(workItemId)}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching remote data collection work item');
    }
    return response.json();
  }

  static async createRemoteDataCollectionRun(
    token: string,
    request: RemoteDataCollectionRefreshRequest
  ): Promise<RemoteDataCollectionRunResponse> {
    const response = await fetch(`${FORGE_REMOTE_DATA_COLLECTION_API_URL}/runs`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error creating remote data collection run');
    }
    return response.json();
  }

  static async fetchRemoteDataCollectionRun(
    token: string,
    runId: string,
    params?: { signal?: AbortSignal }
  ): Promise<RemoteDataCollectionRunResponse> {
    const response = await fetch(
      `${FORGE_REMOTE_DATA_COLLECTION_API_URL}/runs/${encodeURIComponent(runId)}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching remote data collection run');
    }
    return response.json();
  }

  static async fetchMasterDataCollectionInference(
    token: string,
    inferenceId: string,
    params?: {
      chainId?: number | null;
      itemNumber?: string | null;
      normalizedBarcode?: string | null;
      similarityThreshold?: number;
      deltaThreshold?: number;
      mergeLinkedCandidates?: boolean;
      nonAdminPreview?: boolean;
      signal?: AbortSignal;
    }
  ): Promise<MasterDataCollectionDetailResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.itemNumber) query.set('item_number', params.itemNumber);
    if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
    if (params?.similarityThreshold !== undefined) {
      query.set('similarity_threshold', String(params.similarityThreshold));
    }
    if (params?.deltaThreshold !== undefined) {
      query.set('delta_threshold', String(params.deltaThreshold));
    }
    if (params?.mergeLinkedCandidates !== undefined) {
      query.set('merge_linked_candidates', String(params.mergeLinkedCandidates));
    }
    if (params?.nonAdminPreview) query.set('non_admin_preview', 'true');
    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/inferences/${encodeURIComponent(
        inferenceId
      )}${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Master Data Collection inference');
    }
    return response.json();
  }

  static async fetchMasterDataCollectionReviewTargets(
    token: string,
    params: {
      chainId: number;
      itemNumber?: string | null;
      normalizedBarcode?: string | null;
      maxStores?: number;
    }
  ): Promise<RemoteDataCollectionReviewTargetsResponse> {
    const query = new URLSearchParams();
    query.set('chain_id', String(params.chainId));
    if (params.itemNumber) query.set('item_number', params.itemNumber);
    if (params.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
    if (params.maxStores !== undefined) query.set('max_stores', String(params.maxStores));

    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/review-targets?${query.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Remote DC review targets');
    }
    return response.json();
  }

  static async fetchMasterDataCollectionImagePreview(
    token: string,
    imageId: number,
    params?: {
      chainId?: number | null;
      itemNumber?: string | null;
      normalizedBarcode?: string | null;
      signal?: AbortSignal;
    }
  ): Promise<MasterDataCollectionImagePreviewResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.itemNumber) query.set('item_number', params.itemNumber);
    if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);

    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
        String(imageId)
      )}/preview${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Remote DC image preview');
    }
    return response.json();
  }

  static async fetchMasterDataCollectionImageTimeline(
    token: string,
    imageId: number,
    params?: {
      chainId?: number | null;
      itemNumber?: string | null;
      normalizedBarcode?: string | null;
      previousCount?: number;
      signal?: AbortSignal;
    }
  ): Promise<RemoteDataCollectionImageTimelineResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.itemNumber) query.set('item_number', params.itemNumber);
    if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
    if (params?.previousCount !== undefined)
      query.set('previous_count', String(params.previousCount));

    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
        String(imageId)
      )}/timeline${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Remote DC image timeline');
    }
    return response.json();
  }

  static async loadMasterDataCollectionImageInference(
    token: string,
    imageId: number,
    params?: {
      chainId?: number | null;
      itemNumber?: string | null;
      normalizedBarcode?: string | null;
      similarityThreshold?: number;
      deltaThreshold?: number;
      mergeLinkedCandidates?: boolean;
      nonAdminPreview?: boolean;
      cachedOnly?: boolean;
      signal?: AbortSignal;
    }
  ): Promise<MasterDataCollectionDetailResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.itemNumber) query.set('item_number', params.itemNumber);
    if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
    if (params?.similarityThreshold !== undefined) {
      query.set('similarity_threshold', String(params.similarityThreshold));
    }
    if (params?.deltaThreshold !== undefined) {
      query.set('delta_threshold', String(params.deltaThreshold));
    }
    if (params?.mergeLinkedCandidates !== undefined) {
      query.set('merge_linked_candidates', String(params.mergeLinkedCandidates));
    }
    if (params?.nonAdminPreview) query.set('non_admin_preview', 'true');
    if (params?.cachedOnly !== undefined) query.set('cached_only', String(params.cachedOnly));
    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
        String(imageId)
      )}/load-inference${query.toString() ? `?${query.toString()}` : ''}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal: params?.signal,
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error loading Remote DC product recognition');
    }
    return response.json();
  }

  static async fetchMasterDataCollectionDailyPreviews(
    token: string,
    imageId: number,
    params?: {
      chainId?: number | null;
      itemNumber?: string | null;
      normalizedBarcode?: string | null;
      daysBefore?: number;
      daysAfter?: number;
    }
  ): Promise<RemoteDataCollectionDailyPreviewsResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.itemNumber) query.set('item_number', params.itemNumber);
    if (params?.normalizedBarcode) query.set('normalized_barcode', params.normalizedBarcode);
    if (params?.daysBefore !== undefined) query.set('days_before', String(params.daysBefore));
    if (params?.daysAfter !== undefined) query.set('days_after', String(params.daysAfter));

    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/images/${encodeURIComponent(
        String(imageId)
      )}/daily-previews${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Remote DC image previews');
    }
    return response.json();
  }

  static async fetchMasterLabelCropQuery(
    token: string,
    cropId: string,
    params?: {
      chainId?: number | null;
      limit?: number;
    }
  ): Promise<MasterLabelCropQueryResponse> {
    const query = new URLSearchParams();
    if (params?.chainId) query.set('chain_id', String(params.chainId));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));

    const response = await fetch(
      `${FORGE_MASTER_LABEL_API_URL}/crops/${encodeURIComponent(
        cropId
      )}/query${query.toString() ? `?${query.toString()}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error querying Master Label crop');
    }
    return response.json();
  }

  static async createMasterDataCollectionEmbeddingCrops(
    token: string,
    inferenceId: string,
    request: MasterDataCollectionCreateEmbeddingCropsRequest
  ): Promise<MasterDataCollectionCreateEmbeddingCropsResponse> {
    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/inferences/${encodeURIComponent(
        inferenceId
      )}/embedding-crops`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error creating Master Data Collection crops');
    }
    return response.json();
  }

  static async saveMasterDataCollectionMasterLabelState(
    token: string,
    inferenceId: string,
    request: MasterDataCollectionMasterLabelStateSaveRequest
  ): Promise<MasterDataCollectionMasterLabelStateSaveResponse> {
    const response = await fetch(
      `${FORGE_MASTER_DATA_COLLECTION_API_URL}/inferences/${encodeURIComponent(
        inferenceId
      )}/master-label-state`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    ApiService.handleError(response);
    await throwForFailedSaveResponse(
      response,
      'Error saving Master Data Collection Master Label state'
    );
    return response.json();
  }

  static async fetchProductRecognitionInferenceDashboard(
    token: string,
    date: string,
    hour?: number | null,
    chainId?: number | null
  ): Promise<ProductRecognitionInferenceDashboardResponse> {
    const requestKey = `${token}:${date}:${hour ?? 'combined'}:${chainId ?? 'all'}`;
    const cachedResponse = productRecognitionInferenceDashboardCache.get(requestKey);
    if (cachedResponse) {
      return cachedResponse;
    }
    const existingRequest = inFlightProductRecognitionInferenceRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams({ date });
    if (hour !== null && hour !== undefined) params.set('hour', String(hour));
    if (chainId) params.set('chain_id', String(chainId));
    const cacheGeneration = productRecognitionInferenceCacheGeneration;
    const request = (async (): Promise<ProductRecognitionInferenceDashboardResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(
          response,
          'Error fetching Product Recognition inference dashboard'
        );
      }
      const body = await response.json();
      if (cacheGeneration === productRecognitionInferenceCacheGeneration) {
        productRecognitionInferenceDashboardCache.set(requestKey, body);
      }
      return body;
    })();

    inFlightProductRecognitionInferenceRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightProductRecognitionInferenceRequests.delete(requestKey);
    }
  }

  static async fetchProductRecognitionInferenceAggregate(
    token: string,
    dateFrom: string,
    dateTo: string,
    hour?: number | null,
    chainId?: number | null,
    modPrefix?: string | null
  ): Promise<ProductRecognitionInferenceAggregateResponse> {
    const requestKey = `${token}:${dateFrom}:${dateTo}:${hour ?? 'combined'}:${chainId ?? 'all'}:${modPrefix ?? 'all'}`;
    const cachedResponse = productRecognitionInferenceAggregateCache.get(requestKey);
    if (cachedResponse) {
      return cachedResponse;
    }
    const existingRequest = inFlightProductRecognitionInferenceAggregateRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
    });
    if (hour !== null && hour !== undefined) params.set('hour', String(hour));
    if (chainId) params.set('chain_id', String(chainId));
    if (modPrefix) params.set('mod_prefix', modPrefix);
    const cacheGeneration = productRecognitionInferenceCacheGeneration;
    const request = (async (): Promise<ProductRecognitionInferenceAggregateResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/aggregate?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(
          response,
          'Error fetching Product Recognition inference aggregate'
        );
      }
      const body = await response.json();
      if (cacheGeneration === productRecognitionInferenceCacheGeneration) {
        productRecognitionInferenceAggregateCache.set(requestKey, body);
      }
      return body;
    })();

    inFlightProductRecognitionInferenceAggregateRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightProductRecognitionInferenceAggregateRequests.delete(requestKey);
    }
  }

  static async fetchProductRecognitionInferencePlotAnnotations(
    token: string,
    dateFrom: string,
    dateTo: string,
    chainId?: number | null
  ): Promise<ProductRecognitionInferencePlotAnnotationsResponse> {
    const requestKey = `${token}:${dateFrom}:${dateTo}:${chainId ?? 'all'}`;
    const existingRequest = inFlightProductRecognitionInferenceAnnotationRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
    });
    if (chainId) params.set('chain_id', String(chainId));
    const request = (async (): Promise<ProductRecognitionInferencePlotAnnotationsResponse> => {
      const response = await fetch(
        `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/annotations?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(
          response,
          'Error fetching Product Recognition inference daily notes'
        );
      }
      return response.json();
    })();

    inFlightProductRecognitionInferenceAnnotationRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightProductRecognitionInferenceAnnotationRequests.delete(requestKey);
    }
  }

  static async saveProductRecognitionInferencePlotAnnotation(
    token: string,
    request: ProductRecognitionInferencePlotAnnotationRequest
  ): Promise<ProductRecognitionInferencePlotAnnotation> {
    const response = await fetch(
      `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/annotations`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          selected_date: request.selectedDate,
          chain_id: request.chainId,
          note: request.note,
        }),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error saving Product Recognition inference daily note');
    }
    return response.json();
  }

  static async refreshProductRecognitionInferenceDashboard(
    token: string,
    request: ProductRecognitionInferenceRefreshRequest
  ): Promise<ProductRecognitionInferenceRefreshResponse> {
    const response = await fetch(
      `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/refresh`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chain_id: request.chainId,
          date: request.date,
          hour: request.hour,
          hours: request.hours,
          similarity_threshold: request.similarityThreshold,
          delta_threshold: request.deltaThreshold,
          merge_linked_candidates: request.mergeLinkedCandidates,
        }),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(
        response,
        'Error refreshing Product Recognition inference snapshot'
      );
    }
    const body = await response.json();
    clearProductRecognitionInferenceCaches();
    return body;
  }

  static async createProductRecognitionInferenceRefreshRun(
    token: string,
    request: ProductRecognitionInferenceRefreshRequest
  ): Promise<ProductRecognitionInferenceRefreshRunResponse> {
    const response = await fetch(`${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/runs`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chain_id: request.chainId,
        date: request.date,
        hour: request.hour,
        hours: request.hours,
        similarity_threshold: request.similarityThreshold,
        delta_threshold: request.deltaThreshold,
        merge_linked_candidates: request.mergeLinkedCandidates,
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error starting Product Recognition inference refresh');
    }
    const body = await response.json();
    if (body.status === 'completed') clearProductRecognitionInferenceCaches();
    return body;
  }

  static async fetchProductRecognitionInferenceRefreshRun(
    token: string,
    runId: string,
    params?: { signal?: AbortSignal }
  ): Promise<ProductRecognitionInferenceRefreshRunResponse> {
    const response = await fetch(
      `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/runs/${encodeURIComponent(runId)}`,
      { headers: { Authorization: `Bearer ${token}` }, signal: params?.signal }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Product Recognition inference refresh');
    }
    const body = await response.json();
    if (body.status === 'completed') clearProductRecognitionInferenceCaches();
    return body;
  }

  static async fetchItem(
    token: string,
    itemNumber: string,
    chainId: number
  ): Promise<ApiMasterLabelUPCData> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
    });
    const response = await fetch(
      `${FORGE_MASTER_LABEL_API_URL}/item-number/${encodeURIComponent(itemNumber)}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data');
    }
    return response.json();
  }

  static async fetchItemByBarcode(
    token: string,
    barcodeQuery: string,
    chainId: number
  ): Promise<ApiMasterLabelBarcodeLookupResponse> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
    });
    const response = await fetch(
      `${FORGE_MASTER_LABEL_API_URL}/barcode/${encodeURIComponent(barcodeQuery)}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data by barcode');
    }
    return response.json();
  }

  static async fetchItemByDesignId(
    token: string,
    designId: string,
    chainId: number
  ): Promise<ApiMasterLabelDesignLookupResponse> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
    });
    const response = await fetch(
      `${FORGE_MASTER_LABEL_API_URL}/design/${encodeURIComponent(designId)}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data by design id');
    }
    return response.json();
  }

  static async saveState(
    token: string,
    request: MasterLabelStateSaveRequest
  ): Promise<MasterLabelStateSaveResponse> {
    const response = await fetch(`${FORGE_MASTER_LABEL_API_URL}/state`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    await throwForFailedSaveResponse(response, 'Error saving Master Label state');
    return response.json();
  }

  static async fetchItemsByCropIds(
    token: string,
    cropIds: string[],
    chainId: number
  ): Promise<ApiMasterLabelCropLookupResponse> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
    });
    cropIds.forEach((cropId) => params.append('crop_id', cropId));
    const response = await fetch(`${FORGE_MASTER_LABEL_API_URL}/crops?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data by crop ids');
    }
    return response.json();
  }

  static async searchEmbeddingsByCropIds(
    token: string,
    embeddingCropIds: string[]
  ): Promise<CropEmbeddingSearchResponse[]> {
    const params = new URLSearchParams();
    embeddingCropIds.forEach((id) => {
      params.append('embedding_crop_ids', id);
    });
    params.append('include_item_number', 'false');

    const response = await fetch(`${CONTROL_EMBEDDING_API_URL}/by-ids?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching embedding crops by ids');
    }
    return response.json();
  }

  static async resolveMasterLabelLinkTargets(
    token: string,
    request: ApiMasterLabelLinkTargetsRequest
  ): Promise<ApiMasterLabelLinkTargetsResponse> {
    const response = await fetch(`${FORGE_MASTER_LABEL_API_URL}/link-targets`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chain_id: request.chainId,
        item_numbers: request.itemNumbers ?? [],
        normalized_barcodes: request.normalizedBarcodes ?? [],
        design_ids: request.designIds ?? [],
        crop_ids: request.cropIds ?? [],
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error resolving Master Label link targets');
    }
    return response.json();
  }

  static async searchDesignGroups(
    token: string,
    chainId: number,
    query: string,
    page = 1,
    limit = 20
  ): Promise<ForgeDesignGroupSearchResponse> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
      query,
      page: String(page),
      limit: String(limit),
    });
    const response = await fetch(`${FORGE_DESIGN_GROUPS_API_URL}/search?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error searching Forge design groups');
    }
    return response.json();
  }

  static async modifyDesignGroups(
    token: string,
    request: ModifyForgeDesignGroupsRequest
  ): Promise<ModifyForgeDesignGroupsResponse> {
    const response = await fetch(`${FORGE_DESIGN_GROUPS_API_URL}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating Forge design groups');
    }
    return response.json();
  }

  /**
   * Lists capture units awaiting Recognition review for one chain week, worst triage score first.
   * Omitting `weekStart` lets the backend resolve the default week and echo it on the response.
   */
  static async fetchRecognitionReviewQueue(
    token: string,
    params: RecognitionReviewQueueParams
  ): Promise<RecognitionReviewQueueResponse> {
    const query = new URLSearchParams({ chain_id: String(params.chainId) });
    if (params.weekStart) query.set('week_start', params.weekStart);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));

    const requestKey = `${token}:${query.toString()}`;
    const existingRequest = inFlightRecognitionReviewQueueRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<RecognitionReviewQueueResponse> => {
      const response = await fetch(
        `${FORGE_RECOGNITION_REVIEW_API_URL}/queue?${query.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Recognition review queue');
      }
      return response.json();
    })();

    inFlightRecognitionReviewQueueRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightRecognitionReviewQueueRequests.delete(requestKey);
    }
  }

  /** Fetches the weekly Recognition OKR aggregates (both outcome grids, Pareto, capture units). */
  static async fetchRecognitionOkrAggregates(
    token: string,
    params: RecognitionOkrAggregatesParams
  ): Promise<RecognitionOkrAggregatesResponse> {
    const query = new URLSearchParams({ chain_id: String(params.chainId) });
    if (params.weekStart) query.set('week_start', params.weekStart);
    if (params.population) query.set('population', params.population);

    const requestKey = `${token}:${query.toString()}`;
    const existingRequest = inFlightRecognitionOkrAggregateRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<RecognitionOkrAggregatesResponse> => {
      const response = await fetch(
        `${FORGE_RECOGNITION_REVIEW_API_URL}/aggregates?${query.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Recognition OKR aggregates');
      }
      return response.json();
    })();

    inFlightRecognitionOkrAggregateRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightRecognitionOkrAggregateRequests.delete(requestKey);
    }
  }

  /**
   * Fetches one capture unit's samples with their frozen recognition evidence. An omitted `modTag`
   * matches the samples captured without a mod tag, which is a distinct unit from any tagged one.
   */
  static async fetchRecognitionReviewUnit(
    token: string,
    params: RecognitionReviewUnitParams
  ): Promise<RecognitionReviewUnitDetailResponse> {
    const query = new URLSearchParams({ chain_id: String(params.chainId) });
    if (params.modTag) query.set('mod_tag', params.modTag);

    const requestKey = `${token}:${params.imageId}:${query.toString()}`;
    const existingRequest = inFlightRecognitionReviewUnitRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<RecognitionReviewUnitDetailResponse> => {
      const response = await fetch(
        `${FORGE_RECOGNITION_REVIEW_API_URL}/units/${encodeURIComponent(String(params.imageId))}?${query.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Recognition review unit');
      }
      return response.json();
    })();

    inFlightRecognitionReviewUnitRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightRecognitionReviewUnitRequests.delete(requestKey);
    }
  }

  /** Saves one reviewer verdict and returns the outcome the backend scored and stored for it. */
  static async saveRecognitionReview(
    token: string,
    evaluationSampleId: string,
    request: RecognitionReviewRequest
  ): Promise<RecognitionReviewSaveResponse> {
    const response = await fetch(
      `${FORGE_RECOGNITION_REVIEW_API_URL}/samples/${encodeURIComponent(evaluationSampleId)}/review`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error saving Recognition review');
    }
    return response.json();
  }

  /** Stamps every sample of one capture unit with the strategy its crops were collected under. */
  static async setRecognitionSamplingStrategy(
    token: string,
    chainId: number,
    request: RecognitionSamplingStrategyRequest
  ): Promise<RecognitionSamplingStrategyResponse> {
    const query = new URLSearchParams({ chain_id: String(chainId) });
    const response = await fetch(
      `${FORGE_RECOGNITION_REVIEW_API_URL}/units/sampling-strategy?${query.toString()}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating Recognition sampling strategy');
    }
    return response.json();
  }

  /**
   * Fetches an image (e.g. a crop or product photo) as a Blob using session
   * credentials. Shared by flows that re-upload or convert existing images.
   */
  static async fetchImageBlob(url: string): Promise<Blob> {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText} (${url})`);
    }
    return response.blob();
  }

  static async fetchSaveWarnings(
    token: string,
    params: {
      chainId: number;
      tool?: SaveCheckTool;
      checkType?: SaveCheckType;
      status?: SaveWarningStatus;
      reviewOutcome?: SaveWarningReviewOutcome;
      actor?: string;
      createdAfter?: string;
      cursor?: string;
      limit?: number;
    }
  ): Promise<SaveWarningsResponse> {
    const query = new URLSearchParams();
    query.set('chain_id', String(params.chainId));
    if (params.tool !== undefined) query.set('tool', params.tool);
    if (params.checkType !== undefined) query.set('check_type', params.checkType);
    if (params.status !== undefined) query.set('status', params.status);
    if (params.reviewOutcome !== undefined) query.set('review_outcome', params.reviewOutcome);
    if (params.actor !== undefined) query.set('actor', params.actor);
    if (params.createdAfter !== undefined) query.set('created_after', params.createdAfter);
    if (params.cursor !== undefined) query.set('cursor', params.cursor);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    const response = await fetch(`${FORGE_SAVE_WARNINGS_API_URL}?${query.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Forge save-check warnings');
    }
    return response.json();
  }

  static async fetchSaveCheckModes(
    token: string,
    chainId: number
  ): Promise<SaveCheckModesResponse> {
    const query = new URLSearchParams({ chain_id: String(chainId) });
    const response = await fetch(`${FORGE_SAVE_WARNINGS_API_URL}/modes?${query.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Forge save-check enforcement modes');
    }
    return response.json();
  }

  static async fetchSaveWarningChainStats(
    token: string,
    chainIds: number[]
  ): Promise<SaveWarningChainStats[]> {
    const query = new URLSearchParams();
    for (const chainId of chainIds) query.append('chain_id', String(chainId));
    const response = await fetch(`${FORGE_SAVE_WARNINGS_API_URL}/stats?${query.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching Forge save-warning chain statistics');
    }
    const payload: SaveWarningChainStatsResponse = await response.json();
    return payload.stats;
  }

  static async setSaveCheckMode(
    token: string,
    body: { chainId: number; tool: SaveCheckTool; mode: EnforcementMode }
  ): Promise<SaveCheckModesResponse> {
    const response = await fetch(`${FORGE_SAVE_WARNINGS_API_URL}/modes`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chain_id: body.chainId,
        tool: body.tool,
        mode: body.mode,
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error setting Forge save-check enforcement mode');
    }
    return response.json();
  }
}
